/* 예제 S3 저장소 — 공식 코드 예제(블록/파이썬) 등록·수정·삭제·목록.
   plan §6 / S4 핸드오프 / Huenit S3CodeModelRegistry.uploadOfficial(RMW) 패턴.

   ★ 블록/파이썬 manifest 분리 (중요 — 그림·툴패스와 같은 이유)
     앱 enum 은 AssetKind { blocklyCode, aiModel } 뿐 — pythonCode 가 없다. 최상위
     kind 에 새 값을 넣으면 구버전 앱의 AssetKind.values.byName() 이 throw 해 공식
     예제 탭 전체가 죽는다. 그래서 파이썬도 **최상위 kind 는 blocklyCode** 로 내고
     `extras.code_kind='python'`(+`extras.kind=['python']`)로 가른다(mappers.js).
     그런데 구버전 앱은 official/manifest.json 항목을 전부 블록 예제로 취급하므로
     거기에 파이썬을 섞으면 블록 탭에 .py 가 나와 열기가 실패한다. 따라서:
       · 블록(.huenit)  → official/manifest.json           (구버전 앱도 소비)
       · 파이썬(.py)    → official/manifest.python.json     (앱 Python 탭이 소비)
       · 그림·툴패스     → official/manifest.engrave.json    (examples-engrave)
     객체(main.py)+meta.json 은 동일하게 official/code/{id}/ 에 올린다.
     앱 소비 계약 = handoff/APP-python-examples.md (BE-0008).

   ★ 레거시 재발행
     초기 파이썬 자산은 최상위 kind 를 'pythonCode' 로 발행했다 = 앱 파서가 통째로
     skip → 영원히 안 보임. republishLegacyPython() 이 kind 만 blocklyCode 로 고쳐
     meta.json+manifest 를 다시 쓴다(멱등, 다른 필드 무변).

   s3.js(동결 시그니처)만 사용: getJsonAnon/putObject/deleteObject. */
import { getJsonAnon, putObject, deleteObject } from '../../api/s3.js';
import { toAssetMeta, fromAssetMeta, buildManifest } from '../../data/mappers.js';
import { CODE_BUCKET_URL } from '../../config/env.js';
import { computeReorder, sortByOrder, getOrder, compareByOrder } from './ordering.js';

export const BLOCK_MANIFEST_KEY = 'official/manifest.json';
export const PYTHON_MANIFEST_KEY = 'official/manifest.python.json';
export const EXAMPLES_VERSION_KEY = 'official/examples.version.json';

/* 예제 버전 신호 발행 — 앱 version fast-path(cached_example_store)가 이 파일이 로컬
   캐시 버전과 같으면 manifest GET 을 생략한다(26.07.03-admin §1). 따라서 manifest 를
   바꾸는 모든 발행(예제/AI 등록·수정·삭제) 끝에 반드시 호출해 값이 바뀌게 한다.
   값은 비교용 문자열이면 충분 — 타임스탬프 사용. 404(미존재)면 앱은 graceful 폴백. */
export async function bumpExamplesVersion(getCredentials) {
  const nowIso = new Date().toISOString();
  await putObject(getCredentials, EXAMPLES_VERSION_KEY,
    JSON.stringify({ schema: 1, version: nowIso, published_at: nowIso }), 'application/json');
}

const manifestUrl = (key) => (CODE_BUCKET_URL || '') + '/' + key;
const manifestKeyFor = (isPython) => (isPython ? PYTHON_MANIFEST_KEY : BLOCK_MANIFEST_KEY);
const codeKey = (id, isPython) => `official/code/${id}/${isPython ? 'main.py' : 'workspace.huenit'}`;
const metaKey = (id) => `official/code/${id}/meta.json`;
const isPy = (ex) => ex && ex.kind === 'python';

/* 코드 파일 공개 URL (다운로드용) — official/* 은 public-read. */
export function exampleFileUrl(example) {
  return (CODE_BUCKET_URL || '') + '/' + codeKey(example.id, isPy(example));
}

/* ---------- 썸네일(선택) — official/code/{id}/thumbnail.<ext> ---------- */
const THUMB_TYPE_EXT = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/gif': 'gif' };
const THUMB_ALLOWED = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif']);
function thumbExt(file) {
  const dot = file.name ? file.name.lastIndexOf('.') : -1;
  const named = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : '';
  const ext = THUMB_ALLOWED.has(named) ? named : (THUMB_TYPE_EXT[file.type] || 'png');
  return ext === 'jpeg' ? 'jpg' : ext;
}
// 절대 URL → 코드 버킷 객체 key (썸네일 삭제용). engrave 저장소도 재사용.
export function codeKeyFromUrl(url) {
  if (!url) return '';
  if (CODE_BUCKET_URL && url.startsWith(CODE_BUCKET_URL)) return url.slice(CODE_BUCKET_URL.length).replace(/^\//, '');
  try { return new URL(url).pathname.replace(/^\//, ''); } catch (e) { return ''; }
}
/* 썸네일 업로드 → { key, url }. url = 절대 HTTPS(meta.extras.thumbnail 에 기록). */
export async function uploadExampleThumbnail(getCredentials, id, file) {
  const buf = await file.arrayBuffer();
  const key = `official/code/${id}/thumbnail.${thumbExt(file)}`;
  await putObject(getCredentials, key, new Uint8Array(buf), file.type || 'image/png');
  return { key, url: (CODE_BUCKET_URL || '') + '/' + key };
}

/* assetId = 클라 생성 UUIDv4 (plan §6.1). */
export function newAssetId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/* sha256 hex — Web Crypto (plan §6.2). buffer = ArrayBuffer. */
export async function sha256Hex(buffer) {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/* manifest 읽기. 없음(404/403) → 빈 배열. 형식 깨짐 → throw (orphan 쓰기 전 fail-fast).
   engrave 저장소도 이 함수를 재사용한다 — 404/403 관용과 fail-fast 는 안전 계약이라
   복제해서 갈라지면 안 된다. */
export async function readAssets(key) {
  let json;
  try {
    json = await getJsonAnon(manifestUrl(key));
  } catch (e) {
    // 공식 prefix 는 public-read — 익명 GET 의 404(없음) 또는 ListBucket 불가 시 403
    // 둘 다 "객체 없음(빈 목록)"으로 단락(Huenit _fetchManifest 와 동일).
    if (e && (e.status === 404 || e.status === 403)) return [];
    throw new Error(`목록(manifest)을 읽지 못했습니다: ${e && e.message ? e.message : e}`);
  }
  if (!json || !Array.isArray(json.assets)) {
    throw new Error(`${key} 형식이 올바르지 않습니다 (assets 배열 없음) — 덮어쓰기 전 중단합니다.`);
  }
  return json.assets;
}

/* 목록 — 블록(official/manifest.json) + 파이썬(official/manifest.python.json) 병합.
   각 AssetMeta → CMS example(+_meta) 로 변환. 최신순 정렬.
   ★ official/manifest.json 은 AI 예제(kind:'aiModel', R-0156 examples-ai)와 manifest 를
     공유하므로 코드 예제 목록에선 aiModel 을 제외한다(kind 구분 보존 — AI 는 AI 예제 탭이 관리).
   ★ 화이트리스트로 판별한다 — 그림·툴패스(extras.domain='engrave')는 최상위 kind 가
     blocklyCode 라서(BE-0007) 블랙리스트로는 걸러지지 않는다. manifest 가 분리돼 있어
     지금은 섞일 일이 없지만, 나중에 병합되더라도 코드 목록이 오염되지 않도록 방어한다. */
const isCodeAsset = (a) => !!a && a.kind !== 'aiModel'
  && (((a.extras && a.extras.domain) || 'code') === 'code');

export async function listExamples() {
  const [blocks, pythons] = await Promise.all([
    readAssets(BLOCK_MANIFEST_KEY),
    readAssets(PYTHON_MANIFEST_KEY),
  ]);
  const toItem = (meta) => ({ ...fromAssetMeta(meta), _meta: meta });
  const items = [...blocks.filter(isCodeAsset), ...pythons.filter(isCodeAsset)].map(toItem);
  // 배치 순서: (묶음, 번호) 오름차순, 미지정은 뒤(업로드 역순).
  items.sort((a, b) => compareByOrder(a._meta, b._meta));
  return items;
}

/* 등록/수정 (RMW). example = CMS 모델(id,kind,catId,filterIds,title,desc,file).
   fileBuffer(ArrayBuffer) 있으면 새 파일 업로드, 없으면(수정) 기존 객체 유지.
   owner = { email, defaultLang, cats, filters }. onStep(label) = 진행 표시 콜백. */
export async function saveExample(getCredentials, { example, fileBuffer, thumbnailFile, removeThumbnail, owner = {}, onStep }) {
  const python = isPy(example);
  const id = example.id;
  const mKey = manifestKeyFor(python);
  const step = (s) => { if (onStep) onStep(s); };

  step('목록 확인 중…');
  const assets = await readAssets(mKey);                 // 쓰기 전 read = orphan 방지
  const prev = assets.find((a) => a.id === id) || null;

  // 썸네일(선택): 제거 / 새 업로드 / 기존 유지. 제거·교체 시 기존 객체 정리(orphan 방지).
  let thumbnail = example.thumbnail || (prev && prev.extras && prev.extras.thumbnail) || '';
  if (removeThumbnail) {
    if (thumbnail) { try { await deleteObject(getCredentials, codeKeyFromUrl(thumbnail)); } catch (e) { /* ignore */ } }
    thumbnail = '';
  } else if (thumbnailFile) {
    const oldKey = thumbnail ? codeKeyFromUrl(thumbnail) : '';
    step('썸네일 업로드 중…');
    const up = await uploadExampleThumbnail(getCredentials, id, thumbnailFile);
    if (oldKey && oldKey !== up.key) { try { await deleteObject(getCredentials, oldKey); } catch (e) { /* ignore */ } }
    thumbnail = up.url;
  }

  let sha256;
  let sizeBytes;
  if (fileBuffer) {
    sizeBytes = fileBuffer.byteLength;
    step('체크섬 계산 중…');
    sha256 = await sha256Hex(fileBuffer);
    step('코드 파일 업로드 중…');
    await putObject(
      getCredentials,
      codeKey(id, python),
      new Uint8Array(fileBuffer),
      python ? 'text/x-python; charset=utf-8' : 'application/zip',
    );
  } else if (prev) {
    sha256 = prev.sha256;
    sizeBytes = prev.size_bytes;
  } else {
    throw new Error('코드 파일을 업로드하세요.');
  }

  const meta = toAssetMeta({ ...example, thumbnail }, {
    ownerEmail: owner.email,
    sha256,
    sizeBytes,
    defaultLang: owner.defaultLang,
    cats: owner.cats,
    filters: owner.filters,
    uploadedAt: prev ? prev.uploaded_at : new Date().toISOString(),
  });

  step('목록(manifest) 갱신 중…');
  const fresh = await readAssets(mKey);                  // RMW: 쓰기 직전 재읽기로 경쟁 윈도 최소화
  let next = fresh.filter((a) => a.id !== id);
  next.push(meta);

  // 배치 순서: 대상에 (묶음,번호) 있으면 캐스케이드 재번호. 바뀐 '다른' 예제 meta 도 갱신.
  const ord = getOrder(meta);
  if (ord) {
    const changed = computeReorder(next, id, ord.group, ord.index);
    next = next.map((a) => {
      const c = changed[a.id];
      return c ? { ...a, extras: { ...(a.extras || {}), order_group: c.group, order_index: c.index } } : a;
    });
    for (const cid of Object.keys(changed)) {
      if (cid === id) continue;
      const cm = next.find((a) => a.id === cid);
      if (cm) { step('순서 반영 중…'); await putObject(getCredentials, metaKey(cid), JSON.stringify(cm), 'application/json'); }
    }
    if (changed[id]) { meta.extras = next.find((a) => a.id === id).extras; }   // 대상 최종 order 반영
  }

  step('메타데이터 저장 중…');
  await putObject(getCredentials, metaKey(id), JSON.stringify(meta), 'application/json');
  // manifest 는 항상 순서대로 정렬해 기록(앱이 배열 순서로 소비).
  await putObject(getCredentials, mKey, JSON.stringify(buildManifest(sortByOrder(next))), 'application/json');
  step('버전 신호 발행 중…');
  await bumpExamplesVersion(getCredentials);             // 앱 fast-path 갱신 트리거
  return meta;
}

/* ---------- 레거시 파이썬 자산(kind:'pythonCode') 재발행 ---------- */
/* 앱 파서(AssetMeta.maybeFromJson)는 미지 kind 항목을 조용히 버린다 → 목록에서 실종.
   CMS 목록엔 정상으로 보이니(fromAssetMeta 가 관용 파싱) 화면에서 따로 알려야 한다. */
export const isLegacyPythonAsset = (m) => !!m && m.kind === 'pythonCode';

/* kind 만 blocklyCode 로 고쳐 meta.json + manifest 재발행. 반환 = 고친 건수.
   멱등 — 고칠 게 없으면 아무 것도 쓰지 않는다(불필요한 버전 신호 방지). */
export async function republishLegacyPython(getCredentials, onStep) {
  const step = (s) => { if (onStep) onStep(s); };
  step('목록 확인 중…');
  const assets = await readAssets(PYTHON_MANIFEST_KEY);
  const staleIds = assets.filter(isLegacyPythonAsset).map((a) => a.id);
  if (staleIds.length === 0) return 0;

  const next = assets.map((a) => (isLegacyPythonAsset(a) ? { ...a, kind: 'blocklyCode' } : a));
  for (const a of next) {
    if (!staleIds.includes(a.id)) continue;
    step('메타데이터 갱신 중…');
    await putObject(getCredentials, metaKey(a.id), JSON.stringify(a), 'application/json');
  }
  step('목록(manifest) 갱신 중…');
  await putObject(getCredentials, PYTHON_MANIFEST_KEY, JSON.stringify(buildManifest(sortByOrder(next))), 'application/json');
  step('버전 신호 발행 중…');
  await bumpExamplesVersion(getCredentials);             // 앱 fast-path 갱신 트리거
  return staleIds.length;
}

/* 삭제 (RMW) — 객체(코드+meta) + 해당 manifest 엔트리 제거. */
export async function deleteExample(getCredentials, example, onStep) {
  const python = isPy(example);
  const id = example.id;
  const mKey = manifestKeyFor(python);
  const step = (s) => { if (onStep) onStep(s); };

  step('목록 확인 중…');
  const assets = await readAssets(mKey);                 // 쓰기 전 read = orphan 방지
  const prev = assets.find((a) => a.id === id) || null;
  step('파일 삭제 중…');
  await deleteObject(getCredentials, codeKey(id, python));
  await deleteObject(getCredentials, metaKey(id));
  // 썸네일 객체도 정리
  const thumbUrl = prev && prev.extras && prev.extras.thumbnail;
  if (thumbUrl) { try { await deleteObject(getCredentials, codeKeyFromUrl(thumbUrl)); } catch (e) { /* ignore */ } }
  step('목록(manifest) 갱신 중…');
  const next = assets.filter((a) => a.id !== id);
  await putObject(getCredentials, mKey, JSON.stringify(buildManifest(next)), 'application/json');
  step('버전 신호 발행 중…');
  await bumpExamplesVersion(getCredentials);             // 앱 fast-path 갱신 트리거
}
